import axios from 'axios';
import toast from 'react-hot-toast';
import { config as runtimeConfig } from '../config.js';
import { CABECERA_SESION_2FA, esErrorDeDosPasos, manejarErrorDosPasos } from '../lib/dosPasos';
import { solicitudDosPasos, useDosPasosStore } from '../store/dosPasosStore';

const API_URL = runtimeConfig.apiUrl;

const api = axios.create({
  baseURL: `${API_URL}/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const PUBLIC_AUTH_ROUTES = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/apply',
  '/verify-email'
]);

// Prefijos de rutas publicas (matching por startsWith). Util para rutas
// dinamicas tipo /team-invite/:token donde no podemos enumerar paths exactos.
// `/catalogo-contador`: el contador del cliente contesta sin cuenta; un 401 no puede mandarlo al login.
const PUBLIC_PATH_PREFIXES = ['/team-invite', '/catalogo-contador', '/invitacion'];

const isPublicAuthRoute = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  if (PUBLIC_AUTH_ROUTES.has(path)) return true;
  return PUBLIC_PATH_PREFIXES.some(prefix => path.startsWith(prefix + '/') || path === prefix);
};

// Endpoints que no deben gatillar refresh-and-retry (evitar loops)
const isInviteEndpoint = (url = '') => url.includes('/partners/team-invite/');
const isSessionEndpoint = (url = '') => url.includes('/partners/me/session');

const isAuthEndpoint = (url = '') => (
  url.includes('/auth/login') ||
  url.includes('/auth/forgot-password') ||
  url.includes('/auth/reset-password') ||
  url.includes('/auth/verify-email') ||
  url.includes('/partners/auth/login')
);

/**
 * Mensaje de negocio que el servidor mandó, si mandó alguno.
 *
 * Las rutas de partners contestan `{ success:false, error: 'texto' }` — `error` es un STRING, no un
 * objeto. Esta función no contemplaba esa forma, así que devolvía `undefined` y el interceptor
 * pintaba su genérico: el asesor veía a la vez la explicación de verdad («Envíasela al dueño para
 * que la acepte…») y un «Ha ocurrido un error» que solo asusta.
 */
const getApiErrorMessage = (response) => (
  response?.data?.message ||
  response?.data?.error?.message ||
  (typeof response?.data?.error === 'string' ? response.data.error : undefined)
);

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('partnerToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Sesión 2FA del asesor (ver lib/dosPasos.js). Se pone en CADA petición y, si no hay, se
    // quita: una petición repetida tras verificar debe llevar la nueva, no la caducada.
    const sesion2fa = useDosPasosStore.getState().sesion2fa;
    if (sesion2fa) {
      config.headers[CABECERA_SESION_2FA] = sesion2fa;
    } else {
      delete config.headers[CABECERA_SESION_2FA];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response } = error;
    const authEndpoint = isAuthEndpoint(error.config?.url);

    // La API exige la verificación en dos pasos al asesor: se abre el diálogo y, completada,
    // se repite la petición. Si la cancela, la pantalla recibe el error original.
    const reintento = manejarErrorDosPasos(error, {
      solicitud: solicitudDosPasos,
      borrarSesion: () => useDosPasosStore.getState().borrarSesion(),
      repetir: (config) => api.request(config),
    });
    if (reintento) return reintento;

    if (response) {
      switch (response.status) {
        case 401:
          // Evitar redirecciones globales en endpoints/rutas públicas de auth.
          // Así no rompemos el flujo de reset-password cuando hay token local expirado.
          // Un 401 de la 2FA (código mal tecleado, sesión 2FA caducada) tampoco es la sesión del
          // portal: echar al asesor al login le haría perder lo que estaba escribiendo.
          if (
            !isAuthEndpoint(error.config?.url) &&
            !isPublicAuthRoute() &&
            !isInviteEndpoint(error.config?.url) &&
            !esErrorDeDosPasos(response, error.config?.url)
          ) {
            localStorage.removeItem('partnerToken');
            localStorage.removeItem('partnerRefreshToken');
            localStorage.removeItem('partnerUser');
            useDosPasosStore.getState().borrarSesion();
            window.location.href = '/login';
            toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
          }
          break;
        case 403:
          // PERMISSION_REFRESH_NEEDED: el subdoc team fue actualizado tras emitir
          // el JWT. Refrescamos sesion y reintentamos la request original 1 vez.
          if (
            response.data?.code === 'PERMISSION_REFRESH_NEEDED' &&
            !error.config?._refreshRetry &&
            !isSessionEndpoint(error.config?.url)
          ) {
            try {
              error.config._refreshRetry = true;
              await api.get('/partners/me/session');
              // Notificar a la app para que actualice el authStore
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('partner:session-refresh-needed'));
              }
              return api.request(error.config);
            } catch (retryErr) {
              // si el refresh falla, dejar que el flujo siga al toast generico
            }
          }
          // No mostrar toast genérico - dejar que cada componente maneje el error
          if (!getApiErrorMessage(response)) {
            console.warn('[API 403] Respuesta sin mensaje:', response.config?.url, response.data);
          }
          break;
        // Si el servidor explicó qué pasó, lo dice la pantalla: el genérico solo lo contradecía.
        case 404:
          if (!authEndpoint && !getApiErrorMessage(response)) {
            toast.error('Recurso no encontrado.');
          }
          break;
        case 500:
          if (!authEndpoint && !getApiErrorMessage(response)) {
            toast.error('Error del servidor. Por favor, intenta más tarde.');
          }
          break;
        default:
          // Evitar toasts duplicados en auth; esos flujos ya manejan el error en UI.
          // Y cuando el servidor SÍ explicó qué pasó, callar: en este portal cada pantalla
          // muestra ese mensaje (`data.error`), así que añadir el genérico solo duplicaba el
          // aviso — la explicación buena al lado de un «Ha ocurrido un error» que la contradice.
          // Mismo criterio que ya seguía el 403 más arriba.
          if (!authEndpoint && !getApiErrorMessage(response)) {
            toast.error('Ha ocurrido un error.');
          }
      }
    } else if (error.request) {
      toast.error('No se pudo conectar con el servidor.');
    } else {
      toast.error('Error en la solicitud.');
    }

    return Promise.reject(error);
  }
);

export default api;
